if (!customElements.get('slider-controls')) {
  customElements.define('slider-controls', class SliderControls extends HTMLElement {
    constructor() {
      super();
      this._syncRaf = null;
      this._boundScheduleSync = this.scheduleSync.bind(this);
      this._syncViewers = [];
      this._observedButtons = new WeakSet();
      this._observedCounters = new WeakSet();
      this._observedViewers = new WeakSet();
    }

    connectedCallback() {
      if (!this.hasAttribute('data-clone') || !this.dataset.id) return;

      const section = this.closest('.shopify-section');
      if (!section) return;

      const getOriginals = () =>
        Array.from(section.querySelectorAll(`slider-controls[data-id="${this.dataset.id}"][data-origin]`));

      const isCurrent = el =>
        el && (el.offsetParent !== null ||
          el.closest('.tabs__tab-content--active') ||
          (el.closest('.heading-wrapper')?.querySelector('.tabs__list') === null));

      const resolveOriginal = () => {
        const originals = getOriginals();
        if (!originals.length) return null;
        return originals.length === 1 ? originals[0] : (originals.find(isCurrent) || originals[0]);
      };

      if (!getOriginals().length) return;

      const cloneButtons = this.querySelectorAll('button');

      if (cloneButtons.length === 2) {
        const syncDisabled = () => {
          const currentOrig = resolveOriginal();
          const origButtons = currentOrig ? currentOrig.querySelectorAll('button') : [];

          cloneButtons.forEach((btn, i) => {
            const isDisabled = origButtons[i]?.disabled;

            if (isDisabled) {
              btn.setAttribute('disabled', '');
              btn.disabled = true;
            } else {
              btn.removeAttribute('disabled');
              btn.disabled = false;
            }
          });
        };

        cloneButtons.forEach((btn, i) => {
          btn.addEventListener('click', () => {
            const currentOrig = resolveOriginal();
            const targetButton = currentOrig?.querySelectorAll('button')[i];
            targetButton?.click();

            setTimeout(() => syncDisabled(), 50);
          });
        });

        syncDisabled();
        setTimeout(syncDisabled, 0);
        setTimeout(syncDisabled, 100);

        this._buttonObserver = new MutationObserver(() => this.scheduleSync());
        this._syncDisabled = syncDisabled;
      }

      const cloneCounter = this.querySelector('.slider-counter');

      if (cloneCounter) {
        const updateVars = () => {
          const currentOrig = resolveOriginal();
          const origCounter = currentOrig?.querySelector('.slider-counter');
          if (!origCounter) return;

          const current = origCounter.style.getPropertyValue('--slider-counter-current');
          const total = origCounter.style.getPropertyValue('--slider-counter-total');

          if (current) cloneCounter.style.setProperty('--slider-counter-current', current);
          if (total) cloneCounter.style.setProperty('--slider-counter-total', total);
          return current || total;
        };

        updateVars();

        this._styleObserver = new MutationObserver(() => this.scheduleSync());
        this._updateVars = updateVars;
      }

      this._attachObservers = () => {
        getOriginals().forEach(orig => {
          if (this._buttonObserver) {
            orig.querySelectorAll('button').forEach(btn => {
              if (this._observedButtons.has(btn)) return;
              this._observedButtons.add(btn);
              this._buttonObserver.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
            });
          }

          if (this._styleObserver) {
            const counter = orig.querySelector('.slider-counter');
            if (counter && !this._observedCounters.has(counter)) {
              this._observedCounters.add(counter);
              this._styleObserver.observe(counter, { attributes: true, attributeFilter: ['style'] });
            }
          }

          const viewer = orig.closest('[id^="GalleryViewer"], slider-component');
          if (viewer && !this._observedViewers.has(viewer)) {
            this._observedViewers.add(viewer);
            this._syncViewers.push(viewer);
            viewer.addEventListener('slideChanged', this._boundScheduleSync);
          }
        });
      };

      this._attachObservers();
    }

    sync() {
      this._attachObservers?.();
      this._syncDisabled?.();
      return !!(this._updateVars?.() || this._syncDisabled);
    }

    scheduleSync() {
      if (this._syncRaf) return;
      this._syncRaf = requestAnimationFrame(() => {
        this._syncRaf = null;
        this._attachObservers?.();
        this._syncDisabled?.();
        this._updateVars?.();
      });
    }

    disconnectedCallback() {
      this._styleObserver?.disconnect();
      this._buttonObserver?.disconnect();
      if (this._syncRaf) cancelAnimationFrame(this._syncRaf);
      this._syncViewers.forEach((viewer) => viewer.removeEventListener('slideChanged', this._boundScheduleSync));
      this._syncViewers = [];
    }
  });
}
